// packages/web/src/process/useProcessWalker.ts
//
// React hook: runs walkProcess against the active process + walker state,
// derives the EdgeDecoMap for a given scrubber index. All outputs are
// memoized on their inputs.

import { useMemo } from "react";
import type {
  NodeId,
  ProcessModel,
  SubprocessModel,
  WalkerState,
  WalkResult,
  WalkStep,
} from "@archlang/engine";
import { walkProcess } from "@archlang/engine";
import {
  computeEdgeDecoration,
  type EdgeDecoMap,
} from "./edgeDecoration.js";

export interface UseProcessWalkerArgs {
  readonly process: ProcessModel | null;
  readonly state: WalkerState;
  readonly subprocessByName: ReadonlyMap<string, SubprocessModel>;
  /** v0.9.0 §2.5: `do <Process>` fallback targets (subprocess wins on a tie). */
  readonly processByName?: ReadonlyMap<string, ProcessModel>;
  readonly activeStepIndex: number | null;
}

export interface UseProcessWalkerResult {
  /** Walk of only the selected branches (canonical sequence/blockscheme view). */
  readonly walkResult: WalkResult | null;
  /**
   * Walk that materializes every branch's children regardless of
   * selection. Flow uses this so conditional alternatives stay visible.
   */
  readonly walkResultAllBranches: WalkResult | null;
  /**
   * Null when NO walk ran (no process IR) — the board then falls back to
   * detail-index chips. A present-but-EMPTY map means the walk ran and its
   * selected path has zero call steps: every process edge is off-path. The
   * distinction is the signal; consumers must not infer it from size.
   */
  readonly edgeDecoMap: EdgeDecoMap | null;
}

export function useProcessWalker(
  args: UseProcessWalkerArgs,
): UseProcessWalkerResult {
  const { process, state, subprocessByName, processByName, activeStepIndex } = args;

  // The all-branches walk doubles as the canonical step numbering.
  // Every source step (across every branch) gets a globally unique
  // stepIndex here; the selected-only walk borrows those numbers so
  // step identity is stable across branch toggles. Treating a
  // conditional as a "jump" over unselected indices then falls out
  // naturally: scrubbing through the selected walk skips numbers
  // that belong to alternative branches.
  const walkResultAllBranches = useMemo(() => {
    if (!process) return null;
    return walkProcess(process, state, { subprocessByName, processByName }, { showAllBranches: true });
  }, [process, state, subprocessByName, processByName]);

  const walkResult = useMemo(() => {
    if (!process) return null;
    const raw = walkProcess(process, state, { subprocessByName, processByName });
    if (!walkResultAllBranches) return raw;
    return stabilizeIndices(raw, walkResultAllBranches);
  }, [process, state, subprocessByName, processByName, walkResultAllBranches]);

  const edgeDecoMap = useMemo(() => {
    if (!walkResult) return null;
    return computeEdgeDecoration(walkResult, activeStepIndex);
  }, [walkResult, activeStepIndex]);

  return { walkResult, walkResultAllBranches, edgeDecoMap };
}

/**
 * Replace `walk.steps[].stepIndex` with the global numbering from
 * `canonical` (matched by NodeId). Returns the same WalkResult shape
 * with rebuilt byIndex + remapped collapsedGroups / parallelGroups.
 *
 * Mutates the WalkStep objects in place so the WalkResult's tree
 * (whose step leaves are the same object refs) stays consistent.
 *
 * Exported for tests that emulate this hook's pipeline without React —
 * the canonical numbering is the contract the board joins decorations on.
 */
export function stabilizeIndices(walk: WalkResult, canonical: WalkResult): WalkResult {
  const nodeToIndex = new Map<NodeId, number>();
  for (const s of canonical.steps) nodeToIndex.set(s.nodeId, s.stepIndex);
  // Save (old → new) per emission position for range remapping.
  const oldToNew = new Map<number, number>();
  for (const s of walk.steps) {
    const stable = nodeToIndex.get(s.nodeId);
    if (stable !== undefined) oldToNew.set(s.stepIndex, stable);
  }
  const remapRange = (r: { startIndex: number; endIndex: number }) => ({
    startIndex: oldToNew.get(r.startIndex) ?? r.startIndex,
    endIndex: oldToNew.get(r.endIndex) ?? r.endIndex,
  });
  const collapsedGroups = new Map<NodeId, { startIndex: number; endIndex: number }>();
  for (const [k, v] of walk.collapsedGroups) collapsedGroups.set(k, remapRange(v));
  const parallelGroups = new Map<NodeId, { startIndex: number; endIndex: number }>();
  for (const [k, v] of walk.parallelGroups) parallelGroups.set(k, remapRange(v));
  // Mutate stepIndices in place (tree leaves share these refs).
  for (const s of walk.steps) {
    const stable = nodeToIndex.get(s.nodeId);
    if (stable !== undefined) (s as { stepIndex: number }).stepIndex = stable;
  }
  const byIndex = new Map<number, WalkStep>();
  for (const s of walk.steps) byIndex.set(s.stepIndex, s);
  return { ...walk, byIndex, collapsedGroups, parallelGroups };
}
